import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { getFieldsForDataformReadyOnly } from "../utils/drawingUtilities";
import CustomSelect from "../shared/CustomSelect";
import { getDisplayFileName } from "../utils/generalUtilities.js";
import FilePreviewDialog from "../FileExplorer/FilePreviewDialog.jsx";

const MasterList = ({ ladId, docStorePermissions }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const wopiBaseUrl = process.env.REACT_APP_WOPI_BASE_URL;
  const accessToken = sessionStorage.getItem("accessToken");
  const headers = accessToken ? { Authorization: accessToken } : {};

  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [previewFile, setPreviewFile] = useState(null);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const fetchMasterList = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await axios.get(
        `${baseUrl}/explorer/lad/${ladId}/masterlist`,
        { headers }
      );
      if (
        response.data.status === "success" &&
        response.data.data?.length > 0
      ) {
        const grouped = response.data.data.reduce((acc, dataform) => {
          const groupName = dataform?.engg_object?.displayName || "Others";
          if (!groupName.toLowerCase().includes("pipe")) {
            acc[groupName] = acc[groupName] || [];
            acc[groupName].push(dataform);
          }
          return acc;
        }, {});
        setGroupedData(grouped);
      } else {
        setError(true);
      }
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [ladId]);

  useEffect(() => {
    if (ladId && !isNaN(parseInt(ladId))) {
      fetchMasterList();
    } else {
      setLoading(false);
    }
  }, [ladId, fetchMasterList]);

  const flatList = useMemo(() => {
    return Object.entries(groupedData).flatMap(([groupName, items]) =>
      items.map((dataform) => ({
        groupName,
        dataform,
        parsedData: JSON.parse(dataform.dataform_json),
      }))
    );
  }, [groupedData]);

  const totalPages = Math.ceil(flatList.length / rowsPerPage);
  const paginatedData = flatList.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  if (!ladId || isNaN(parseInt(ladId))) {
    return <div className="text-center p-5">Loading...</div>;
  }
  if (loading) {
    return <div className="text-center p-5">Loading...</div>;
  }
  if (error) {
    return <div className="text-danger p-3">Something went wrong!</div>;
  }

  return (
    <div className="p-3">
      <table className="analysis-modal-table table">
        <thead className="table-dark">
          <tr>
            <th className="fnt12">S.No</th>
            <th className="fnt12">Equipment ID</th>
            <th className="fnt12">Type</th>
            <th className="fnt12">Make</th>
            <th className="fnt12">Model</th>
            <th className="fnt12">Serial</th>
            <th className="fnt12">DataSheet</th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.map(({ groupName, dataform, parsedData }, idx) => {
            const globalIndex = (currentPage - 1) * rowsPerPage + idx;
            const readonlyFields = getFieldsForDataformReadyOnly(
              parsedData,
              groupName
            );

            const equipmentId =
              Object.entries(readonlyFields).find(([key]) =>
                key.toLowerCase().includes("equipment id")
              )?.[1] ||
              dataform.engg_object_name ||
              `Component ${globalIndex + 1}`;

            const makeName =
              Object.entries(readonlyFields).find(([key]) =>
                key.toLowerCase().includes("make")
              )?.[1] || `-`;

            const modelName =
              Object.entries(readonlyFields).find(([key]) =>
                key.toLowerCase().includes("model")
              )?.[1] || `-`;

            const serialName =
              Object.entries(readonlyFields).find(([key]) =>
                key.toLowerCase().includes("serial")
              )?.[1] || `-`;

            return (
              <React.Fragment key={globalIndex}>
                <tr>
                  <td>{globalIndex + 1}</td>
                  <td>{equipmentId}</td>
                  <td>{groupName}</td>
                  <td>{makeName}</td>
                  <td>{modelName}</td>
                  <td>{serialName}</td>
                  <td>
                    <a
                      href="#!"
                      className="text-primary text-decoration-underline"
                      onClick={(e) => {
                        e.preventDefault();
                        setExpandedIndex(
                          expandedIndex === globalIndex ? null : globalIndex
                        );
                      }}
                    >
                      {equipmentId}
                    </a>
                  </td>
                </tr>
                {expandedIndex === globalIndex && (
                  <tr className="bg-light">
                    <td colSpan={7}>
                      <div className="p-3 border rounded">
                        <h6 className="mb-3">{equipmentId} Details</h6>

                        <div className="row g-3">
                          <div className="col-md-4 border rounded p-2">
                            {Object.entries(readonlyFields).map(
                              ([field, value], fieldIdx) => (
                                <div
                                  className="py-2 d-flex justify-content-between border-bottom"
                                  key={fieldIdx}
                                >
                                  <div className="small text-secondary">
                                    {field}
                                  </div>
                                  <div className="fnt12">{value}</div>
                                </div>
                              )
                            )}
                          </div>

                          <div className="col-md-8 border rounded p-2">
                            <div className="small text-secondary mb-2">
                              Attachments
                            </div>
                            <div className="fnt12">
                              <div>
                                {dataform?.Attachment?.length > 0 ? (
                                  dataform.Attachment.map(
                                    (attachment, index) => {
                                      const file = attachment.file;
                                      const encodedKey = encodeURIComponent(
                                        file.key
                                      );
                                      const viewUrl = `${wopiBaseUrl}/api/api/file/download?key=${encodedKey}`;

                                      return (
                                        <div
                                          className="text-primary"
                                          key={file.id}
                                          style={{ cursor: "pointer" }}
                                          onDoubleClick={() =>
                                            setPreviewFile({
                                              fileUrl: viewUrl,
                                              fileName: file.name,
                                            })
                                          }
                                          title="Double-click to open"
                                        >
                                          🔗 {getDisplayFileName(file?.name)}
                                        </div>
                                      );
                                    }
                                  )
                                ) : (
                                  <div>No attachments found.</div>
                                )}

                                {previewFile && (
                                  <FilePreviewDialog
                                    file={previewFile}
                                    onClose={() => setPreviewFile(null)}
                                    docStorePermissions={docStorePermissions}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {parsedData?.staticForm?.fieldNotes && (
                          <div className="mt-3">
                            <strong className="d-block mb-1">
                              Field Notes:
                            </strong>
                            <div className="border rounded p-2 bg-white">
                              {parsedData.staticForm.fieldNotes}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Pagination controls */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex align-items-center">
          <label className="fnt12 fw-500 text-secondary me-2">
            Rows per page:
          </label>
          <CustomSelect
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            options={[10, 25, 50, 100]}
          />
        </div>

        <div className="d-flex align-items-center">
          <MdKeyboardArrowLeft
            className={`me-2 ${currentPage === 1 ? "disabled" : ""}`}
            onClick={() => {
              if (currentPage > 1) {
                handlePageChange(currentPage - 1);
              }
            }}
            style={{
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              opacity: currentPage === 1 ? 0.5 : 1,
              fontSize: "1.5rem",
            }}
          />
          <span className="mx-2 fnt12 fw-500 text-secondary">
            Page {currentPage} of {totalPages}
          </span>
          <MdKeyboardArrowRight
            className={`ms-2 ${currentPage === totalPages ? "disabled" : ""}`}
            onClick={() => {
              if (currentPage < totalPages) {
                handlePageChange(currentPage + 1);
              }
            }}
            style={{
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              opacity: currentPage === totalPages ? 0.5 : 1,
              fontSize: "1.5rem",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default MasterList;
